import { CAKE_TYPE } from "../../../constants/pageConstants/pdpConstants";
import { getProductDeliveryType, processDeliveryDate } from "../../../utils/common";

/**
 * Custom hook that returns the `cleverTapData` object containing various properties.
 * The object is used to track product and delivery details for analytics or reporting purposes.
 *
 * @param {Object} params - The parameters needed to build the cleverTapData object.
 * @param {Object} params.currentItem - The current item object containing product information.
 * @param {string} params.clientVariantType - The type of the client variant (e.g., with or without egg).
 * @param {Object} params.CAKE_TYPE - An object containing constants for cake variants, including WITH_EGG.
 * @param {Object} params.nextAvailableDifference - Object containing the next available date difference.
 * @param {boolean} params.isSameDateSelected - Flag indicating if the same date is selected.
 * @param {boolean} params.isSameShippingSelected - Flag indicating if the same delivery method is selected.
 * @param {string} params.userSelectedTimeSlotId - ID of the selected time slot for delivery.
 * @param {boolean} params.isDateToday - Flag indicating if the selected date is today.
 * @param {boolean} params.isDateTomorrow - Flag indicating if the selected date is tomorrow.
 * @returns {Object} - The `cleverTapData` object.
 */
const useCleverTapCartData = ({
  currentItem,
  clientVariantType,
  nextAvailableDifference,
  isSameDateSelected,
  isSameShippingSelected,
  userSelectedTimeSlotId,
  isDateToday,
  isDateTomorrow,
}) => {
  return {
    product_name: currentItem?.productName,
    pid: currentItem?.productId,
    product_category: currentItem?.primaryProductCategoryId,
    product_price: currentItem?.price,
    is_egg: clientVariantType?.toUpperCase() === CAKE_TYPE.WITH_EGG ?? false,
    delivery_type: getProductDeliveryType(currentItem?.mainProduct?.primaryProductCategoryId),
    pincode: currentItem?.pincode,
    area: currentItem?.deliveryLocation,
    system_pincode: currentItem?.pincode,
    country: currentItem?.productCatalogId,
    city: currentItem?.deliveryLocation,
    is_international: currentItem?.productCatalogId !== "india",
    date_of_delivery: processDeliveryDate(currentItem?.deliveryDate),
    sku_id: currentItem?.skuCode,
    delivery_shipping: currentItem?.shippingMethodName || "",
    delivery_shipping_fee: currentItem?.shippingDetails?.shippingPrice ?? 0,
    delivery_slot: currentItem?.shippingDetails?.deliveryTimeSlot,
    next_available_date_difference: nextAvailableDifference?.nextAvailableDateDiff,
    is_same_date_selected: isSameDateSelected,
    is_same_delivery_selected: isSameShippingSelected,
    is_same_slot_selected: userSelectedTimeSlotId,
    is_date_today: isDateToday,
    is_date_tomorrow: isDateTomorrow,
  };
};

export default useCleverTapCartData;
